import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import React, { lazy, useEffect, Suspense } from "react";
import GuestRoute from "./routes/GuestRoute";

import LiveLecture from "./components/livekit/LiveLecture";
import {
  auth0Audience,
  auth0ClientID,
  auth0Domain,
} from "./constant/AppConstant";
import {
  Auth0Provider,
  useAuth0,
  withAuthenticationRequired,
} from "@auth0/auth0-react";
import ProtectedRoute from "./routes/ProtectedRoute";
import { useSelector } from "react-redux";
import {
  useGetSchoolClassroomsQuery,
  useLazyGetCourseDetailsDataQuery,
} from "./APIConnect";
import { JobPostForm } from "./components/Admin/JobBoard/JobPost";
import JobBoardDashboard from "./components/Admin/JobBoard/Dashboard";
import JobOpportunities from "./components/Student/JobBoard/JobOpportunities";
import { JobApplications } from "./components/Admin/JobBoard/JobApplication";
import JobApplication from "./components/Student/JobBoard/JobApplication";
// import { DiscussionRoutes } from './routes/DiscussionRoutes';
import InnovationPodDashboard from "./components/Student/InnovationPod/InnovationPodDashboard";
import PodWorkspace from "./components/Student/InnovationPod/PodWorkspace";

const PodRoom = lazy(() => import("components/Student/InnovationPod/PodRoom"));
const PodFiles = lazy(() => import("components/Student/InnovationPod/PodFiles"));
const PeerForumPage = lazy(() => import("components/student-dashboard/PeerForumPage"));
const MyProgressPage = lazy(() => import("components/student-dashboard/MyProgressPage"));
const RenkaiPage = lazy(() => import("components/student-dashboard/RenkaiPage"));
const CalendarPage = lazy(() => import("components/student-dashboard/CalendarPage"));


// Lazy load components
const LoginPage = lazy(() => import("components/Login/Login"));
const TestPage = lazy(() => import("components/Test/Test"));
const DashboardPage = lazy(() =>
  import("components/Student/Dashboard/Dashboard")
);
const AllCoursesPage = lazy(() =>
  import("components/Admin/AllCourses/AllCourses")
);
const ContentCreationPage = lazy(() =>
  import("components/Admin/Content/ContentCreation")
);
const ContentViewPage = lazy(() =>
  import("components/Student/ContentView/ContentView")
);
const CreationFormPage = lazy(() =>
  import("components/Admin/Forms/CreationForm")
);
const ContentMediaPage = lazy(() =>
  import("components/Student/ContentMedia/ContentMedia")
);
const EnhancedLearningPage = lazy(() =>
  import("components/student-dashboard/enhanced-learning-page")
);

const NewCourseDetailsPage = lazy(() =>
  import("components/student-dashboard/course-detail/course-detail-page")
);

const VoiceAssessmentPage = lazy(() =>
  import("components/student-dashboard/VoiceAssessmentPage")
);

const CourseDetailV2 = lazy(() =>
  import("components/student-dashboard/course-detail-v2/CourseDetailV2")
);

const ProjectCenterPage = lazy(() =>
  import("components/student-dashboard/project-center/ProjectCenterPage")
);

const ProjectDetailsPage = lazy(() =>
  import("components/student-dashboard/project-center/ProjectDetailsPage")
);

const QuestionCreationPage = lazy(() =>
  import("components/Admin/QuestionCreation/QuestionCreation")
);
const QuestionsPage = lazy(() =>
  import("components/Admin/Questions/QuestionsPage")
);
const QuestionsEditor = lazy(() =>
  import("components/Admin/Questions/QuestionEditor")
);
const AssignmentsPage = lazy(() =>
  import("components/Admin/Assignments/AssignmentsPage")
);
const QuizViewPage = lazy(() => import("components/Student/Quiz/QuizView"));
const ReportCardPage = lazy(() =>
  import("components/Student/ReportCard/ReportCardPage")
);
const QAPage = lazy(() => import("components/Student/QA/QAPage"));
const InitalScreenPage = lazy(() =>
  import("components/Student/InitalScreen/InitalScreenPage")
);
const AdminCoursesPage = lazy(() =>
  import("components/Admin/Courses/CoursesPage")
);
const AdminCoursesEditor = lazy(() =>
  import("components/Admin/Courses/CourseEditor")
);
const CourseManagementNew = lazy(() =>
  import("components/Admin/Courses/CourseManagementNew")
);
const AdminSchoolsPage = lazy(() =>
  import("components/Admin/Schools/SchoolsPage")
);
const InstructorDashboardV2 = lazy(() => import("components/teacher-dashboard/components/InstructorDashboardV2"));
const AdminSchoolEditor = lazy(() =>
  import("components/Admin/Schools/SchoolEditor")
);
const AdminClassroomEditor = lazy(() =>
  import("components/Admin/Classrooms/ClassroomEditor")
);
const AdminClassroomsPage = lazy(() =>
  import("components/Admin/Classrooms/ClassroomsPage")
);
const AdminUsersPage = lazy(() => import("components/Admin/Users/UsersPage"));
const AdminUsersEditor = lazy(() =>
  import("components/Admin/Users/UserEditor")
);
const StudentFeedbackPage = lazy(() =>
  import("components/Admin/StudentFeedback/StudentFeedbackPage")
);
const StudentFeedbackView = lazy(() =>
  import("components/Student/StudentFeedback/StudentFeedbackView")
);
const UserProfileEditor = lazy(() =>
  import("components/User/UserProfileEditor")
);
const SubtopicFormPage = lazy(() =>
  import("components/Admin/Courses/SubtopicFormPage")
);
const AdminSubjectsPage = lazy(() =>
  import("components/Admin/Subjects/SubjectsPage")
);
const AdminSubjectEditor = lazy(() =>
  import("components/Admin/Subjects/SubjectEditor")
);
const AdminProjectManagement = lazy(() =>
  import("components/Admin/Projects/ProjectManagement")
);
// Teacher Components
const TeacherClassroomPage = lazy(() =>
  import("components/Teacher/Classroom/TeacherClassroomPage")
);
const TeacherClassroomDetailsPage = lazy(() =>
  import("components/Teacher/Classroom/TeacherClassroomDetailsPage")
);
const TeacherQueriesPage = lazy(() =>
  import("components/Teacher/Queries/TeacherQueriesPage")
);

const TeacherMySpaceDialog = lazy(() =>
  import("components/teacher-dashboard/components/my-space-page")
);
const AdminMySpacePage = lazy(() =>
  import("components/Admin/MySpace/AdminMySpacePage")
);

const StudentAnalytics = lazy(() =>
  import("components/Analytics/StudentAnalytics")
);
const TeacherAnalytics = lazy(() =>
  import("components/Analytics/TeacherAnalytics")
);
const DiscussionsPage = lazy(() =>
  import("components/Student/Discussions/DiscussionsPage")
);
const DiscussionManagement = lazy(() =>
  import("components/Discussions/DiscussionManagement")
);

const DiscussionRoutes = lazy(() => import("./routes/DiscussionRoutes"));

const NewDashboard = lazy(() =>
  import("components/student-dashboard//NewDashboardPage")
);

const StudentDashboardV2 = lazy(() =>
  import("components/Student/Dashboard/StudentDashboardV2")
);
const StudentDashboardv3 = lazy(() =>
  import("components/Student/Dashboard/StudentDashboardv3")
);

const MyClassrooms = lazy(() =>
  import("components/Student/MyClassrooms/MyClassrooms")
);


const Portfolio = lazy(() =>
  import("components/Student/Portfolio/Portfolio")
);


const VyudiLearnPage = lazy(() =>
  import("components/student-dashboard/vyudilearn-course/VyudiLearnPage")
);

const StudentHubPage = lazy(() =>
  import("components/student-dashboard/StudentHubPage")
);

const AdminDashboard = lazy(() =>
  import("components/Admin/AdminDashboard")
);
const ContentCreatorDashboard = lazy(() =>
  import("components/Creator/CreatorDashboard")
);
const UserManagementPage = lazy(() =>
  import("components/Admin/Dashboard/UserManagementPage")
);
const AdminRecordingRequests = lazy(() => import("components/Admin/RecordingRequests/AdminRecordingRequests"));
const CourseCreatorRecordings = lazy(() => import("components/Admin/RecordingRequests/CourseCreatorRecordings"));
const InstructorRecordingRequests = lazy(() => import("components/teacher-dashboard/components/InstructorRecordingRequests"));
const RecordingLiveSession = lazy(() => import("components/teacher-dashboard/components/RecordingLiveSession"));
const AdminLiveKitSessions = lazy(() => import("components/Admin/LiveKit/AdminLiveKitSessions"));
const FacultyAnalyticsDashboard = lazy(() => import("components/faculty/FacultyAnalyticsDashboard"));
const FacultyDashboard = lazy(() =>
  import("components/FacultyPortal/FacultyDashboard")
);
const GuestRecordingSession = lazy(() => import("components/livekit/GuestRecordingSession"));
const EmployabilityIndexPage = lazy(() => import("components/EmployabilityIndex/EmployabilityIndex"));

const InstructorDashboard = lazy(() => import("components/teacher-dashboard/components/InstructorDashboard"));
const InstructorQueriesPage = lazy(() => import("components/teacher-dashboard/components/InstructorQueriesPage"));
const InstructorClassSubmissions = lazy(() => import("components/teacher-dashboard/components/InstructorClassSubmissions"));
const InstructorSubmissionDetail = lazy(() => import("components/teacher-dashboard/components/InstructorSubmissionDetail"));
const InstructorAIEvaluationPage = lazy(() => import("components/teacher-dashboard/components/InstructorAIEvaluationPage"));
const InnovationPodManagement = lazy(() => import("components/Admin/InnovationPod/InnovationPodManagement"));
const InnovationPodChat = lazy(() => import("components/Admin/InnovationPod/InnovationPodChat"));
const UnifiedQueriesPage = lazy(() => import("components/Admin/Queries/UnifiedQueriesPage"));
const AdminAnalyticsDashboard = lazy(() => import("components/Admin/Analytics/AdminAnalyticsDashboard"));
const ExpertManagement = lazy(() => import("components/Admin/Users/ExpertManagement"));
const ExpertDashboard = lazy(() =>
  import("components/ExpertPortal/ExpertDashboard")
);

const RoutesComponent = () => {
  const { userIsRendered } = useSelector((state) => state.user);
  const { user } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      const roleId = user["http://learnido-app/roleId"];
      // Do not auto-redirect on public guest pages
      if (location.pathname === "/" && !location.pathname.startsWith('/guest/')) {
        if (roleId === "1") {
          navigate("/admin/dashboard");
        } else if (roleId === "2") {
          navigate("/school-admin/courses");
        } else if (roleId === "3") {
          navigate("/teacher/classroom");
        } else if (roleId === "4") {
          navigate("/my-classrooms");
        } else if (roleId === "6") {
          // Default landing for instructor: My Space
          navigate("/instructor/my-space");
        } else if (roleId === "5") {
          // Default landing for content creator: Creator Dashboard
          navigate("/admin/courses");
        } else if (roleId === "7") {
          // Faculty default to dashboard
          navigate("/faculty/dashboard");
        } else if (roleId === "8") {
          // Industry Expert default to dashboard
          navigate("/expert/dashboard");
        }
      }
    }
  }, [user, location.pathname, navigate]);

  // Hard-bypass all auth/navigation for public guest routes
  if (location.pathname.startsWith('/guest/')) {
    return (
      <Routes>
        <Route path="/guest/recording-session" element={<GuestRecordingSession />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public guest recording session (no login) placed first to avoid auth gating */}
      <Route path="/guest/recording-session" element={<GuestRecordingSession />} />
      <Route path="/analytics" element={<EmployabilityIndexPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/profile" element={<UserProfileEditor />} />
      {userIsRendered && user ? (
        user["http://learnido-app/roleId"] === "4" ? (
          <Route>
            <Route path="/" element={<Navigate to="/my-classrooms" replace />} />
            <Route path="/dashboard" element={<StudentDashboardV2 />} />
            <Route path="/dashboard-v3" element={<StudentDashboardv3 />} />
            <Route
              path="/enhanced-learning"
              element={<EnhancedLearningPage />}
            />
            <Route path="/courses" element={<NewCourseDetailsPage />} />
            <Route path="/courses-v2" element={<CourseDetailV2 />} />
            <Route path="/voice-assessment" element={<VoiceAssessmentPage />} />
            <Route path="/vyudi-learn" element={<VyudiLearnPage />} />
            <Route path="/student-hub" element={<StudentHubPage />} />
            <Route path="/dashboard-v2" element={<NewDashboard />} />

            <Route path="/project-center" element={<ProjectCenterPage />} />
            <Route
              path="/innovation-pod"
              element={<InnovationPodDashboard />}
            />
            <Route
              path="/innovation-pod/workspace"
              element={<PodWorkspace />}
            />
            <Route
              path="/innovation-pod/pod-room"
              element={<PodRoom />}
            />
            <Route
              path="/innovation-pod/pod-room/:podId"
              element={<PodRoom />}
            />
            <Route
              path="/innovation-pod/pod-files"
              element={<PodFiles />}
            />
            <Route
              path="/innovation-pod/pod-files/:podId"
              element={<PodFiles />}
            />

            <Route
              path="/project-center/:projectId"
              element={<ProjectDetailsPage />}
            />
            <Route path="/peer-forum" element={
              <Suspense fallback={<div>Loading...</div>}>
                <PeerForumPage />
              </Suspense>
            } />
            <Route path="/peer-forum/:id" element={
              <Suspense fallback={<div>Loading...</div>}>
                <PeerForumPage />
              </Suspense>
            } />
            <Route path="/my-progress" element={
              <Suspense fallback={<div>Loading...</div>}>
                <MyProgressPage />
              </Suspense>
            } />
            <Route path="/discussions/*" element={
              <Navigate to="/peer-forum" replace />
            } />
            <Route
              path="/reportCard"
              element={
                <StudentAnalytics
                  classroomId={null}
                  studentId={user.sub?.replace("auth0|", "")}
                  schoolId={user["http://learnido-app/schoolId"]}
                />
              }
            />
            <Route
              path="/qa"
              element={
                <QAPage
                  studentId={user.sub?.replace("auth0|", "")}
                  schoolId={user["http://learnido-app/schoolId"]}
                />
              }
            />
            <Route
              path="/job-opportunities"
              element={<JobOpportunities />}
            />
            <Route
              path="/apply/:jobId"
              element={<JobApplication />}
            />
            <Route path="/my-feedback" element={<StudentFeedbackView />} />
            <Route path="/my-classrooms" element={<MyClassrooms />} />
            <Route path="/portfolio" element={
              <Suspense fallback={<div>Loading...</div>}>
                <Portfolio />
              </Suspense>
            } />
            <Route path="/renkai" element={
              <Suspense fallback={<div>Loading...</div>}>
                <RenkaiPage />
              </Suspense>
            } />
            <Route path="/calendar" element={
              <Suspense fallback={<div>Loading...</div>}>
                <CalendarPage />
              </Suspense>
            } />
          </Route>
        ) : user["http://learnido-app/roleId"] === "1" ? (
          <Route>
            {/** App Admin routes */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/content-creator" element={<ContentCreatorDashboard />} />
            <Route path="/admin/instructor" element={
              <Suspense fallback={<div>Loading...</div>}>
                <InstructorDashboardV2 />
              </Suspense>
            } />
            <Route path="/admin/users" element={<UserManagementPage />} />
            <Route path="/admin/expert-management" element={<ExpertManagement />} />
            <Route path="/admin/questions/:id" element={<QuestionsEditor />} />
            <Route
              path="/admin/questions/create"
              element={<QuestionsEditor />}
            />
            <Route path="/admin/queries" element={<UnifiedQueriesPage />} />
            <Route path="/admin/assignments" element={<AssignmentsPage />} />
            <Route path="/admin/livekit" element={<AdminLiveKitSessions />} />
            <Route path="/admin/recording-requests" element={<AdminRecordingRequests />} />
            <Route path="/admin/courses" element={<AdminCoursesPage />} />
            <Route path="/admin/courses-new" element={<CourseManagementNew />} />
            <Route path="/admin/courses/:id" element={<AdminCoursesEditor />} />
            <Route
              path="/admin/courses/create"
              element={<AdminCoursesEditor />}
            />
            <Route path="/admin/courses/subtopic/create" element={<SubtopicFormPage />} />
            <Route path="/admin/courses/subtopic/edit/:id" element={<SubtopicFormPage />} />
            <Route path="/admin/schools" element={<AdminSchoolsPage />} />
            <Route path="/admin/schools/:id" element={<AdminSchoolEditor />} />
            <Route
              path="/admin/classrooms/:id"
              element={<AdminClassroomEditor />}
            />
            <Route
              path="/admin/schools/create"
              element={<AdminSchoolEditor />}
            />
            <Route path="/admin/users" element={<UserManagementPage />} />
            <Route path="/admin/users/:id" element={<AdminUsersEditor />} />
            <Route path="/admin/users/create" element={<AdminUsersEditor />} />
            <Route path="/admin/classrooms" element={<AdminClassroomsPage />} />
            <Route path="/admin/analytics" element={<AdminAnalyticsDashboard />} />
            <Route path="/admin/student-feedback" element={<StudentFeedbackPage />} />

            <Route path="/admin/projects" element={<AdminProjectManagement />} />
            <Route path="/admin/my-space" element={<AdminMySpacePage />} />

            <Route path="/admin/subjects" element={<AdminSubjectsPage />} />
            <Route
              path="/admin/subjects/create"
              element={<AdminSubjectEditor />}
            />
            <Route
              path="/admin/subjects/:id"
              element={<AdminSubjectEditor />}
            />
            <Route
              path="/school-admin/discussions/*"
              element={
                <Suspense fallback={<div>Loading...</div>}>
                  <DiscussionRoutes />
                </Suspense>
              }
            />

            <Route path="/admin/job-board" element={<JobBoardDashboard />} />
            <Route path="/admin/innovation-pod" element={<InnovationPodManagement />} />
            <Route path="/admin/innovation-pod/:podId" element={<InnovationPodManagement />} />
            <Route path="/admin/innovation-pod/:podId/chat" element={<InnovationPodChat />} />
          </Route>
        ) : user["http://learnido-app/roleId"] === "2" ? (
          <Route>
            {/** School Admin routes */}
            <Route
              path="/school-admin/courses"
              element={<AdminCoursesPage />}
            />
            <Route
              path="/school-admin/courses/:id"
              element={<AdminCoursesEditor />}
            />
            <Route path="/admin/courses/subtopic/create" element={<SubtopicFormPage />} />
            <Route path="/admin/courses/subtopic/edit/:id" element={<SubtopicFormPage />} />
            <Route path="/school-admin/users" element={<AdminUsersPage />} />
            <Route
              path="/school-admin/users/:id"
              element={<AdminUsersEditor />}
            />
            <Route
              path="/school-admin/users/create"
              element={<AdminUsersEditor />}
            />
            <Route
              path="/school-admin/classrooms"
              element={<AdminClassroomsPage />}
            />
            <Route
              path="/school-admin/classrooms/:id"
              element={<AdminClassroomEditor />}
            />
            <Route
              path="/school-admin/classrooms/create"
              element={<AdminClassroomEditor />}
            />
            <Route
              path="/school-admin/discussions/*"
              element={
                <Suspense fallback={<div>Loading...</div>}>
                  <DiscussionRoutes />
                </Suspense>
              }
            />
          </Route>
        ) : user["http://learnido-app/roleId"] === "3" ? (
          <Route>
            {/** Teacher routes */}
            <Route path="/" element={<TeacherClassroomPage />} />
            <Route
              path="/teacher/classroom"
              element={<TeacherClassroomPage />}
            />
            <Route
              path="/teacher/classroom/:classroomId"
              element={<TeacherClassroomDetailsPage />}
            />
            <Route path="/teacher/analytics" element={<TeacherAnalytics />} />
            <Route
              path="/teacher/my-space"
              element={
                <Suspense fallback={<div>Loading...</div>}>
                  <TeacherMySpaceDialog />
                </Suspense>
              }
            />
            <Route
              path="/teacher/discussions"
              element={
                <Suspense fallback={<div>Loading...</div>}>
                  <DiscussionRoutes />
                </Suspense>
              }
            />
            <Route path="/teacher/innovation-pod" element={<InnovationPodManagement />} />
            <Route path="/teacher/innovation-pod/:podId" element={<InnovationPodManagement />} />
            <Route path="/teacher/innovation-pod/:podId/chat" element={<InnovationPodChat />} />
            {/* Alias fallback for admin prefix */}
            <Route path="/admin/innovation-pod" element={<InnovationPodManagement />} />
            <Route path="/admin/innovation-pod/:podId" element={<InnovationPodManagement />} />
            <Route path="/admin/innovation-pod/:podId/chat" element={<InnovationPodChat />} />
            <Route
              path="/teacher/submissions"
              element={
                <div className="container mx-auto p-6">
                  <InstructorClassSubmissions />
                </div>
              }
            />
            <Route
              path="/teacher/submissions/:classroomId/:studentId/:assignmentId"
              element={
                <div className="container mx-auto p-6">
                  <InstructorSubmissionDetail />
                </div>
              }
            />
            <Route
              path="/teacher/ai-evaluation/:classroomId/:studentId/:assignmentId"
              element={<InstructorAIEvaluationPage />}
            />
          </Route>
        ) : user["http://learnido-app/roleId"] === "6" ? (
          <Route>
            <Route path="/instructor" element={<InstructorDashboard />} />
            <Route path="/instructor/dashboard" element={<InstructorDashboard initialTab="myspace" />} />
            <Route path="/instructor/my-space" element={<InstructorDashboard initialTab="myspace" />} />
            <Route path="/instructor/queries" element={<InstructorQueriesPage />} />
            <Route path="/instructor/discussions" element={
              <Suspense fallback={<div>Loading...</div>}>
                <DiscussionRoutes />
              </Suspense>
            } />
            <Route path="/instructor/livekit" element={<InstructorDashboard initialTab="livekit" />} />
            <Route path="/instructor/recording-requests" element={<InstructorRecordingRequests />} />
            <Route path="/instructor/recording-session/:requestId" element={<RecordingLiveSession />} />
            <Route path="/instructor/submissions" element={<InstructorDashboard initialTab="submissions" />} />
            <Route path="/teacher/submissions" element={
              <div className="container mx-auto p-6">
                <InstructorClassSubmissions />
              </div>
            } />
            <Route
              path="/teacher/submissions/:classroomId/:studentId/:assignmentId"
              element={
                <div className="container mx-auto p-6">
                  <InstructorSubmissionDetail />
                </div>
              }
            />
            <Route
              path="/teacher/ai-evaluation/:classroomId/:studentId/:assignmentId"
              element={<InstructorAIEvaluationPage />}
            />
            <Route path="/instructor/notifications" element={<InstructorDashboard initialTab="notifications" />} />
            <Route path="/instructor/innovation-pod" element={<InnovationPodManagement />} />
            <Route path="/instructor/innovation-pod/:podId" element={<InnovationPodManagement />} />
            <Route path="/instructor/innovation-pod/:podId/chat" element={<InnovationPodChat />} />
            <Route path="/instructor/student-feedback" element={<StudentFeedbackPage />} />
            {/* Alias fallback for admin prefix */}
            <Route path="/admin/innovation-pod" element={<InnovationPodManagement />} />
            <Route path="/admin/innovation-pod/:podId" element={<InnovationPodManagement />} />
            <Route path="/admin/innovation-pod/:podId/chat" element={<InnovationPodChat />} />
          </Route>
        ) : user["http://learnido-app/roleId"] === "7" ? (
          <Route>
            <Route path="/faculty/dashboard" element={<FacultyDashboard />} />
            <Route path="/faculty/analytics" element={<FacultyAnalyticsDashboard />} />
            <Route path="/instructor/discussions" element={
              <Suspense fallback={<div>Loading...</div>}>
                <DiscussionRoutes />
              </Suspense>
            } />
            <Route path="/" element={<Navigate to="/faculty/dashboard" replace />} />
          </Route>
        ) : user["http://learnido-app/roleId"] === "5" ? (
          <Route>
            {/* ContentWriter: All admin features + Creator Dashboard */}
            <Route path="/admin/content-creator" element={<ContentCreatorDashboard />} />
            <Route path="/admin/questions" element={<QuestionsPage />} />
            <Route path="/admin/questions/:id" element={<QuestionsEditor />} />
            <Route path="/admin/questions/create" element={<QuestionsEditor />} />
            <Route path="/admin/assignments" element={<AssignmentsPage />} />
            <Route path="/admin/courses" element={<AdminCoursesPage />} />
            <Route path="/admin/courses/:id" element={<AdminCoursesEditor />} />
            <Route path="/admin/courses/create" element={<AdminCoursesEditor />} />
            <Route path="/admin/courses/subtopic/create" element={<SubtopicFormPage />} />
            <Route path="/admin/courses/subtopic/edit/:id" element={<SubtopicFormPage />} />
            <Route path="/admin/schools" element={<AdminSchoolsPage />} />
            <Route path="/admin/schools/:id" element={<AdminSchoolEditor />} />
            <Route path="/admin/classrooms/:id" element={<AdminClassroomEditor />} />
            <Route path="/admin/schools/create" element={<AdminSchoolEditor />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/users/:id" element={<AdminUsersEditor />} />
            <Route path="/admin/users/create" element={<AdminUsersEditor />} />
            <Route path="/admin/projects" element={<AdminProjectManagement />} />
            <Route path="/admin/my-space" element={<AdminMySpacePage />} />
            <Route path="/admin/livekit" element={<AdminLiveKitSessions />} />
            <Route path="/admin/recordings" element={<CourseCreatorRecordings />} />
            <Route path="/admin/student-feedback" element={<StudentFeedbackPage />} />
            <Route path="/admin/subjects" element={<AdminSubjectsPage />} />
            <Route path="/admin/subjects/create" element={<AdminSubjectEditor />} />
            <Route path="/admin/subjects/:id" element={<AdminSubjectEditor />} />
            <Route path="/admin/innovation-pod" element={<InnovationPodManagement />} />
            <Route path="/admin/innovation-pod/:podId" element={<InnovationPodManagement />} />
            <Route path="/admin/innovation-pod/:podId/chat" element={<InnovationPodChat />} />
            {/* No dashboard or discussions for ContentWriter */}
          </Route>
        ) : user["http://learnido-app/roleId"] === "8" ? (
          <Route>
            <Route path="/expert/dashboard" element={<ExpertDashboard />} />
            <Route path="/industry-expert/discussions" element={
              <Suspense fallback={<div>Loading...</div>}>
                <DiscussionRoutes />
              </Suspense>
            } />
            <Route path="/" element={<Navigate to="/expert/dashboard" replace />} />
          </Route>
        ) : (
          <Route path="/" element={<LoginPage />} />
        )
      ) : (
        <Route path="/" element={<LoginPage />} />
      )}
      {/* Discussion Forum Routes */}
      <Route
        path="/discussions/*"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <DiscussionRoutes />
          </Suspense>
        }
      />
      <Route path="/live-session/:classroomId" element={<LiveLecture />} />
      {/* Public guest recording session (no login) */}
      <Route path="/guest/recording-session" element={<GuestRecordingSession />} />
    </Routes>
  );
};

export default RoutesComponent;
